'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect } from 'react';
import { scroller } from 'react-scroll';
import { COMMENT_SORT_TYPE, COMMENT_TYPE } from '@/api';
import { SELECTOR } from '@/constants';
import { useToastPoint } from '@/hooks/useToastPoint';
import { CONTENT_CATEGORY, GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
const COMMENT_HASH_SCROLL_TIMEOUT_MS = 1_500;
const COMMENT_CONTENT_CATEGORY = {
  [COMMENT_TYPE.POLICY]: CONTENT_CATEGORY.POLICY,
  [COMMENT_TYPE.POLICY_INFO]: CONTENT_CATEGORY.POLICY_NEWS,
  [COMMENT_TYPE.POLICY_CARD]: CONTENT_CATEGORY.POLICY_CARD,
  [COMMENT_TYPE.HOMETOWN_NEWS]: CONTENT_CATEGORY.HOMETOWN_NEWS,
  [COMMENT_TYPE.COMMUNITY]: CONTENT_CATEGORY.COMMUNITY
} as const;

// window 스크롤 기준으로 댓글 리스트 시작 위치를 다시 계산한다.
const scrollToCommentListTop = (behavior: ScrollBehavior = 'auto') => {
  const commentListTopElement = document.getElementById(SELECTOR.COMMENT_LIST_TOP);
  if (!commentListTopElement) return;
  const targetRect = commentListTopElement.getBoundingClientRect();
  const nextTop = window.scrollY + targetRect.top - 100;
  window.scrollTo({
    top: Math.max(nextTop, 0),
    behavior
  });
};

// 서버 반영 후 새 댓글/수정 댓글 위치로 자연스럽게 이동시키는 공통 함수다.
export const scrollToCommentTarget = (commentId: number) => {
  setTimeout(() => {
    scroller.scrollTo(SELECTOR.COMMENT_PREFIX + commentId, {
      duration: 300,
      smooth: 'easeOutQuad',
      offset: -100
    });
  }, 300);
};

// URL hash 기반 진입 시 이미지 로딩 등으로 위치가 흔들려도 잠시 동안 다시 맞춰준다.
export const useCommentThreadHashScroll = pathname => {
  const $ = _c(2);
  let t0;
  if ($[0] !== pathname) {
    t0 = [pathname];
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useLayoutEffect(_temp, t0);
};
interface UseCommentPostSuccessParams {
  /** GTM 카테고리 분기와 정렬 reset 판단에 사용 */
  commentType: COMMENT_TYPE;
  /** GTM 전송용 원문 id */
  contentId: number | string;
  /** GTM용 댓글 대상 제목 */
  title?: string;
  /** 정책 관련 화면의 GTM용 정책명 */
  policyName?: string;
  /** 현재 목록 정렬 상태 */
  sortType: COMMENT_SORT_TYPE;
  /** 작성 직후 최신순으로 되돌리는 함수 */
  resetSortType: () => void;
}

// 댓글 작성 성공 시 공통 후처리(GTM, 정렬 복원, 포인트 토스트, 스크롤)를 묶는다.
export const useCommentPostSuccess = t0 => {
  const $ = _c(8);
  const {
    commentType,
    contentId,
    title,
    policyName,
    sortType,
    resetSortType
  } = t0;
  const {
    toastPoint
  } = useToastPoint();
  let t1;
  if ($[0] !== commentType || $[1] !== contentId || $[2] !== policyName || $[3] !== resetSortType || $[4] !== sortType || $[5] !== title || $[6] !== toastPoint) {
    t1 = (commentId, point) => {
      trackGtmEvent(GTM_EVENT_TYPE.POST_COMMENT, {
        content_name: policyName,
        comment_name: title,
        content_category: COMMENT_CONTENT_CATEGORY[commentType],
        content_id: contentId.toString()
      });
      if (sortType !== COMMENT_SORT_TYPE.LATEST) {
        resetSortType();
      }
      scrollToCommentListTop("smooth");
      if (point) {
        toastPoint(point);
      }
      scrollToCommentTarget(commentId);
    };
    $[0] = commentType;
    $[1] = contentId;
    $[2] = policyName;
    $[3] = resetSortType;
    $[4] = sortType;
    $[5] = title;
    $[6] = toastPoint;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
function _temp() {
  if (typeof window === "undefined") {
    return;
  }
  if (window.location.hash !== `#${SELECTOR.COMMENT_LIST_TOP}`) {
    return;
  }
  const startTime = performance.now();
  let frameId = null;
  scrollToCommentListTop();
  const keepCommentListAligned = () => {
    if (performance.now() - startTime >= COMMENT_HASH_SCROLL_TIMEOUT_MS) {
      return;
    }
    scrollToCommentListTop();
    frameId = window.requestAnimationFrame(keepCommentListAligned);
  };
  frameId = window.requestAnimationFrame(keepCommentListAligned);
  return () => {
    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
    }
  };
}